import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

export default function AccessManagementModal({ form, companyId, onClose, onSave }) {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accessMode, setAccessMode] = useState('all'); // 'all' or 'restricted'

  useEffect(() => {
    loadUsersAndAccess();
  }, [form, companyId]);

  async function loadUsersAndAccess() {
    setLoading(true);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      // Load all users in the company
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('companyId', '==', companyId));
      const snapshot = await getDocs(q);
      
      let usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // If no users found, add current user as fallback
      if (usersList.length === 0 && currentUser) {
        usersList = [{
          id: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || 'Current User',
          role: 'Owner'
        }];
      }
      
      setUsers(usersList);
      
      // Load all groups in the company (stored as array in company document)
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        const groupsArray = companyData.groups || [];
        // Convert group names to objects with id and name
        const groupsList = groupsArray.map((groupName, index) => ({
          id: groupName, // Use the group name as ID for simplicity
          name: groupName
        }));
        setGroups(groupsList);
      } else {
        setGroups([]);
      }
      
      // Check if form has access restrictions
      const formAccessUsers = form.accessList || [];
      const formAccessGroups = form.accessGroups || [];
      const hasAllUsersFlag = form.allUsers === true;
      
      if (hasAllUsersFlag || (formAccessUsers.length === 0 && formAccessGroups.length === 0 && !form.accessList && !form.accessGroups)) {
        setAccessMode('all');
        setSelectedUsers([]);
        setSelectedGroups([]);
      } else {
        setAccessMode('restricted');
        setSelectedUsers(formAccessUsers);
        setSelectedGroups(formAccessGroups);
      }
    } catch (error) {
      console.error('Error loading users and groups:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const formRef = doc(db, `companies/${companyId}/forms`, form.id);
      
      // If access mode is "all", clear the access lists and set allUsers flag
      const updateData = accessMode === 'all' 
        ? { 
            accessList: [], 
            accessGroups: [],
            allUsers: true,
            updatedAt: new Date() 
          }
        : { 
            accessList: selectedUsers,
            accessGroups: selectedGroups,
            allUsers: false,
            updatedAt: new Date() 
          };
      
      await updateDoc(formRef, updateData);
      
      // Get updated form data
      const updatedDoc = await getDoc(formRef);
      const updatedForm = { id: updatedDoc.id, ...updatedDoc.data() };
      
      onSave(updatedForm);
      onClose();
    } catch (error) {
      console.error('Error saving access list:', error);
      alert('Failed to save access settings');
    } finally {
      setSaving(false);
    }
  }

  function toggleUser(userId) {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  }

  function toggleGroup(groupId) {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b p-6 flex justify-between items-center">
          <h2 className="text-xl font-bold">Manage Access: {form.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">Loading users...</div>
          ) : (
            <div className="space-y-4">
              {/* Access Mode Selection */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="accessMode"
                    checked={accessMode === 'all'}
                    onChange={() => setAccessMode('all')}
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <div className="font-medium">All Users</div>
                    <div className="text-sm text-gray-500">Anyone in the company can access this form</div>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 border rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="accessMode"
                    checked={accessMode === 'restricted'}
                    onChange={() => setAccessMode('restricted')}
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Restricted Access</div>
                    <div className="text-sm text-gray-500">Only selected users and groups can access this form</div>
                  </div>
                </label>
              </div>
              
              {/* User Selection (only shown in restricted mode) */}
              {accessMode === 'restricted' && (
                <div className="space-y-3 pt-4 border-t">
                  {/* Users Section */}
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Users
                    </p>
                    {users.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        <p>No other users found in this company.</p>
                        <p className="mt-2">Invite team members to share this form.</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          Select which users can access this form:
                        </p>
                        
                        {users.map(user => (
                          <label
                            key={user.id}
                            className="flex items-center gap-3 p-3 border rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => toggleUser(user.id)}
                              className="w-4 h-4"
                            />
                            <div className="flex-1">
                              <div className="font-medium">{user.displayName || user.email}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                            {user.role && (
                              <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded">
                                {user.role}
                              </span>
                            )}
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                  
                  {/* Groups Section */}
                  <div className="space-y-3 pt-4 border-t">
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Groups
                    </p>
                    {groups.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        <p>No groups found in this company.</p>
                        <p className="mt-2">Create groups to organize team members.</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          Select which groups can access this form:
                        </p>
                        
                        {groups.map(group => (
                          <label
                            key={group.id}
                            className="flex items-center gap-3 p-3 border rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedGroups.includes(group.id)}
                              onChange={() => toggleGroup(group.id)}
                              className="w-4 h-4"
                            />
                            <div className="flex-1">
                              <div className="font-medium">{group.name}</div>
                            </div>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t p-6 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
